import { useEffect, useState } from "react";

/** The bank-card size check. An outline the size of a bank card (ISO/IEC 7810 ID-1,
 *  85.60 × 53.98 mm) is drawn from the screen's known size; the patient holds a card
 *  against the screen and makes the outline match it. The size they settle on, relative
 *  to the drawn one, is the result: 1 when the screen shows true millimetres. A magnifier
 *  (Windows Magnifier, macOS Zoom), the monitor's own zoom or overscan, or a wrong size
 *  on record all move it away from 1. Chrome's page zoom is allowed for (devicePixelRatio
 *  includes it). */
export function CardCheck({ screenSizeMm, screenPx, cardMm, tolerance, busy, onDone, onSkip }: {
  screenSizeMm: [number, number]; screenPx: [number, number]; cardMm: [number, number]; tolerance: number;
  busy?: boolean; onDone: (scale: number) => void; onSkip?: () => void;
}) {
  const [scale, setScale] = useState(1);
  const [dpr, setDpr] = useState(window.devicePixelRatio || 1);
  useEffect(() => {
    const t = window.setInterval(() => setDpr(window.devicePixelRatio || 1), 500);
    return () => window.clearInterval(t);
  }, []);
  const cssPerMm = screenPx[0] / screenSizeMm[0] / dpr;
  const w = cardMm[0] * cssPerMm * scale;
  const h = cardMm[1] * cssPerMm * scale;
  const r = 3.18 * cssPerMm * scale;               // the card's corner radius
  const step = (d: number) => setScale((s) => Math.min(1.4, Math.max(0.6, Math.round((s + d) * 1000) / 1000)));
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" || e.key === "ArrowUp" || e.key === "+") { step(0.005); e.preventDefault(); }
    if (e.key === "ArrowLeft" || e.key === "ArrowDown" || e.key === "-") { step(-0.005); e.preventDefault(); }
  };
  return (
    <div className="card-check" onKeyDown={onKey}>
      <ol className="steps">
        <li>Take a bank card (or any card that size: a library card or a driving licence).</li>
        <li>Hold it flat against the screen, its top-left corner on the outline's top-left corner.</li>
        <li>Make the outline bigger or smaller until it is exactly the size of the card.</li>
      </ol>
      <div className="card-stage">
        <div className="card-outline" style={{ width: `${w}px`, height: `${h}px`, borderRadius: `${r}px` }}
          aria-label="card outline" role="img">
          <span className="card-corner">put the card's corner here</span>
        </div>
      </div>
      <div className="row card-controls">
        <button className="big secondary" aria-label="Smaller" onClick={() => step(-0.005)}>− Smaller</button>
        <input type="range" min={0.6} max={1.4} step={0.005} value={scale} aria-label="Outline size"
          onChange={(e) => setScale(Number(e.target.value))} style={{ flex: 1 }} />
        <button className="big secondary" aria-label="Bigger" onClick={() => step(0.005)}>+ Bigger</button>
      </div>
      <p className="small muted">Fine steps: the arrow keys. A match within {Math.round(tolerance * 100)}% passes.</p>
      <div className="row">
        <button className="big" disabled={busy} onClick={() => onDone(scale)}>The outline matches my card</button>
        {onSkip && <button className="secondary" disabled={busy} onClick={onSkip}>Skip for now</button>}
      </div>
    </div>
  );
}
